<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from '@/composables/useI18n'
import { useEvents } from '@/composables/useEvents'
import { useFilters } from '@/composables/useFilters'
import { useAuth } from '@/composables/useAuth'
import { useToasts } from '@/composables/useToasts'
import { usePublishing } from '@/composables/usePublishing'
import { renderMarkdown } from '@/markdown'
import { formatDateTime, formatStatusBadgeLabel, isDimmed, isSuccess } from '@/utils'
import type { Entry, Update } from '@/types'

const props = defineProps<{
  entry: Entry
  detailsOpen: boolean
}>()

const emit = defineEmits<{
  (e: 'toggle-details', id: string, open: boolean): void
  (e: 'edit-entry', entry: Entry): void
  (e: 'publish-entry', entryId: string): void
  (e: 'delete-entry', entryId: string): void
}>()

const { lang, t, getEventTranslation, getTypeLabel, getSeverityLabel, getStatusLabel } = useI18n()
const { findServiceByName, findComponentByName, getServiceLabel, getComponentLabel } = useEvents()
const { toggleChip } = useFilters()
const { isAuthenticated } = useAuth()
const { showToast } = useToasts()
const { isEntryPublishing, getProgress, getProgressPercent } = usePublishing()

// Publishing state for this entry
const publishing = computed(() => isEntryPublishing(props.entry.id))
const publishProgress = computed(() => getProgress(props.entry.id))
const publishPercent = computed(() => getProgressPercent(props.entry.id))

// Sort order for updates (per-card is fine, but we use global default)
const updatesSortDesc = ref(localStorage.getItem('updatesSortOrder') !== 'asc')

const title = computed(() => getEventTranslation(props.entry.title))
const summary = computed(() => getEventTranslation(props.entry.summary))
const detailsHtml = computed(() => renderMarkdown(getEventTranslation(props.entry.details)))

const serviceLabel = computed(() => {
  const svc = findServiceByName(props.entry.service)
  return getServiceLabel(svc, lang.value)
})

const componentLabel = computed(() => {
  if (!props.entry.component) return ''
  const comp = findComponentByName(props.entry.component)
  return getComponentLabel(comp, lang.value)
})

const timeRange = computed(() => {
  const start = formatDateTime(new Date(props.entry.start_at))
  if (props.entry.end_at) {
    return `${start} – ${formatDateTime(new Date(props.entry.end_at))}`
  }
  return `${start} – ${t('ongoing')}`
})

const typeLabel = computed(() => getTypeLabel(props.entry.type))
const severityLabel = computed(() => getSeverityLabel(props.entry.severity))
const statusLabel = computed(() => {
  const label = getStatusLabel(props.entry.status)
  return isSuccess(props.entry.status) ? `✅ ${label}` : label
})

const dimmed = computed(() => isDimmed(props.entry.status))

const sortedUpdates = computed(() => {
  if (!props.entry.updates?.length) return []
  return [...props.entry.updates].sort((a, b) => {
    const timeA = new Date(a.at).getTime()
    const timeB = new Date(b.at).getTime()
    return updatesSortDesc.value ? timeB - timeA : timeA - timeB
  })
})

function toggleSort() {
  updatesSortDesc.value = !updatesSortDesc.value
  localStorage.setItem('updatesSortOrder', updatesSortDesc.value ? 'desc' : 'asc')
}

function onDetailsToggle(e: Event) {
  const details = e.target as HTMLDetailsElement
  emit('toggle-details', props.entry.id, details.open)
}

function onMarkerClick(e: Event) {
  e.stopPropagation()
  const permalink = `${window.location.origin}${window.location.pathname}#/${props.entry.id}`
  window.location.hash = `/${props.entry.id}`
  navigator.clipboard.writeText(permalink).then(() => {
    showToast('Permalink copied to clipboard')
  }).catch(() => {})
}

function handleBadgeFilter(kind: 'type' | 'severity' | 'status', value: string) {
  toggleChip(kind, value)
}

function updateStatusLabel(status: string): string {
  const label = getStatusLabel(status)
  return isSuccess(status) ? `✅ ${label}` : label
}

function updateMessage(message: string | Record<string, string>): string {
  if (typeof message === 'string') return message
  return getEventTranslation(message)
}

const activeHash = ref(window.location.hash)

function handleHashChange() {
  activeHash.value = window.location.hash
}

onMounted(() => window.addEventListener('hashchange', handleHashChange))
onBeforeUnmount(() => window.removeEventListener('hashchange', handleHashChange))

// Hash-based highlight detection
const isActive = computed(() => {
  const hash = activeHash.value
  if (!hash || !hash.startsWith('#/')) return false
  return hash.slice(2) === props.entry.id
})
</script>

<template>
  <div
    :id="entry.id"
    :class="['timeline-item', entry.type, { dimmed, 'active-target': isActive }]"
  >
    <div class="timeline-marker" @click="onMarkerClick"></div>
    <div
      :class="['timeline-card', { 'unpublished': isAuthenticated && (entry.state === 'draft' || entry.state === 'edited') }]"
      :data-unpublished="isAuthenticated && entry.state === 'draft' ? t('unpublished').toUpperCase() : (isAuthenticated && entry.state === 'edited' ? t('edited').toUpperCase() : '')"
    >
      <div class="card-header">
        <h3 class="card-title">{{ title }}</h3>
        <div class="card-badges">
          <span
            :class="['badge', 'badge-type', entry.type, { 'badge-dimmed': dimmed }]"
            style="cursor: pointer"
            @click.stop="handleBadgeFilter('type', entry.type)"
          >{{ typeLabel }}</span>
          <span
            :class="['badge', 'badge-severity', entry.severity, { 'badge-dimmed': dimmed }]"
            style="cursor: pointer"
            @click.stop="handleBadgeFilter('severity', entry.severity)"
          >{{ severityLabel }}</span>
          <span
            :class="['badge', 'badge-status', { 'status-success': isSuccess(entry.status) }]"
            style="cursor: pointer"
            @click.stop="handleBadgeFilter('status', entry.status)"
          >{{ statusLabel }}</span>
        </div>
        <div v-if="isAuthenticated" class="card-actions">
          <div
            v-if="(entry.state === 'draft' || entry.state === 'edited') && !publishing"
            class="publish-btn-wrapper"
          >
            <button
              type="button"
              class="btn btn-ghost"
              style="color: #10b981; font-weight: 600"
              @click.stop="emit('publish-entry', entry.id)"
            >Publish</button>
          </div>
          <div
            v-else-if="publishing"
            class="publish-progress-wrapper"
          >
            <div class="publish-progress-container">
              <div class="publish-progress-bar" :style="{ width: `${publishPercent}%` }"></div>
              <div class="publish-progress-text">
                {{ publishProgress ? `${publishProgress.sent_count}/${publishProgress.total_targets}` : '...' }}
              </div>
            </div>
          </div>
          <button
            type="button"
            class="btn btn-ghost"
            @click.stop="emit('edit-entry', entry)"
          >Edit</button>
          <button
            type="button"
            class="btn btn-ghost"
            @click.stop="emit('delete-entry', entry.id)"
          >Delete</button>
        </div>
      </div>
      <div class="card-meta">
        <div class="meta-item">
          <span class="label">{{ t('service-label') }}</span>
          <span class="value service-name">{{ serviceLabel }}</span>
        </div>
        <div v-if="entry.component" class="meta-item component-item">
          <span class="label">{{ t('component-label') }}</span>
          <span class="value component-name">{{ componentLabel }}</span>
        </div>
        <div class="meta-item">
          <span class="label">{{ t('time-label') }}</span>
          <span class="value time-range">{{ timeRange }}</span>
        </div>
      </div>
      <div class="card-summary">{{ summary }}</div>
      <details class="card-details" :open="detailsOpen || undefined" @toggle="onDetailsToggle">
        <summary>{{ t('details-label') }}</summary>
        <div class="details-content">
          <div class="details-text" v-html="detailsHtml"></div>
          <div v-if="sortedUpdates.length" class="updates-log">
            <div class="updates-header">
              <h4>{{ t('updates-label') }}</h4>
              <button
                type="button"
                class="btn btn-sort"
                :title="t('toggle-sort-order')"
                :aria-label="t('toggle-sort-order')"
                @click.stop="toggleSort"
              >
                <span class="sort-arrow">{{ updatesSortDesc ? '↓' : '↑' }}</span>
              </button>
            </div>
            <div class="updates-list">
              <div
                v-for="(update, idx) in sortedUpdates"
                :key="idx"
                :class="['update-item', { 'status-success': isSuccess(update.status) }]"
              >
                <div class="update-time">{{ formatDateTime(new Date(update.at)) }}</div>
                <div :class="['update-badge', { 'status-success': isSuccess(update.status) }]">
                  {{ updateStatusLabel(update.status) }}
                </div>
                <div class="update-message" v-html="renderMarkdown(updateMessage(update.message))"></div>
              </div>
            </div>
          </div>
        </div>
      </details>
    </div>
  </div>
</template>
